import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import Table from '../../Table/Table';
import apiClient from '../../../pages/keycloak/axiosInstance';

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
const ALLOWED_MRN_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.pdf', '.doc', '.docx', '.mp4', '.mov', '.avi'];
const ALLOWED_EVIDENCE_EXTENSIONS = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
const ALLOWED_VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi'];

const FileUpload = ({ rowId, field, files, handleFileUpload, handleRemoveFile, disabled, label, accept }) => (
  <div className="flex flex-col gap-3">
    <label className="flex items-center gap-3 bg-white border border-gray-300 rounded-lg p-3 cursor-pointer hover:bg-gray-50 disabled:cursor-not-allowed">
      <input
        type="file"
        multiple
        onChange={(e) => handleFileUpload(rowId, field, e.target.files)}
        className="hidden"
        disabled={disabled}
        accept={accept}
      />
      <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
      </svg>
      <span className="text-sm text-gray-500">{label}</span>
    </label>
    {files.length > 0 && (
      <div className="flex flex-col gap-2 max-h-24 overflow-y-auto">
        {files.map((file, index) => (
          <div key={index} className="flex items-center gap-3 bg-gray-100 p-2 rounded-lg">
            <span className="text-sm text-gray-700 truncate flex-1">{file.name}</span>
            <button
              onClick={() => handleRemoveFile(rowId, field, index)}
              className="text-red-500 hover:text-red-700 p-1 disabled:opacity-50"
              disabled={disabled}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);

const SelectInput = ({ value, onChange, options, disabled, placeholder }) => (
  <select
    value={value}
    onChange={onChange}
    className="border p-3 rounded-lg w-full bg-white focus:ring-2 focus:ring-gray-500 shadow-sm text-sm"
    disabled={disabled}
  >
    <option value="">{placeholder}</option>
    {options.map((option, index) => (
      <option key={index} value={option.value || option}>{option.label || option}</option>
    ))}
  </select>
);

const DamagedForm = () => {
  const [serialNumbers, setSerialNumbers] = useState([]);
  const [serialNoInput, setSerialNoInput] = useState('');
  const [rows, setRows] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowLoading, setRowLoading] = useState({});
  const [showTable, setShowTable] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);
  const projectId = localStorage.getItem('selectedProject') || '';
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchContractors = async () => {
      try {
        if (!projectId) throw new Error('No project selected');
        const response = await apiClient.post(`get_contractor_names`, { projectId }, {
          headers: { 'Content-Type': 'application/json' },
        });
        setContractors(response.data.data);
      } catch (err) {
        setResponseMessage({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to fetch contractors' });
      }
    };
    fetchContractors();
  }, [projectId]);

  useEffect(() => {
    if (responseMessage) {
      const timer = setTimeout(() => setResponseMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [responseMessage]);

  const addSerialNumber = () => {
    if (!serialNoInput.trim() || serialNumbers.includes(serialNoInput)) {
      setResponseMessage({
        type: 'error',
        text: serialNoInput.trim() ? 'Serial number already added' : 'Please enter a valid serial number',
      });
      return;
    }
    setSerialNumbers([...serialNumbers, serialNoInput]);
    setSerialNoInput('');
    setResponseMessage({ type: 'success', text: 'Serial number added successfully' });
  };

  const removeSerialNumber = (serial) => {
    setSerialNumbers(serialNumbers.filter((s) => s !== serial));
    setResponseMessage({ type: 'success', text: 'Serial number removed successfully' });
  };

  const clearSerialNumbers = () => {
    setSerialNumbers([]);
    setShowTable(false);
    setRows([]);
    setResponseMessage({ type: 'success', text: 'All serial numbers cleared' });
  };

  const fetchModuleDetails = async () => {
    if (!serialNumbers.length) {
      setResponseMessage({ type: 'error', text: 'Please add at least one serial number' });
      return;
    }
    setLoading(true);
    try {
      const response = await apiClient.post(`get_module_details`, {
        projectId,
        module_numbers: serialNumbers,
      }, { headers: { 'Content-Type': 'application/json' } });

      if (response.data.status === 'error') {
        setResponseMessage({ type: 'error', text: response.data.message });
        setLoading(false);
        return;
      }

      const moduleData = response.data.data;
      setRows(moduleData.map((module, index) => ({
        id: index + 1,
        moduleSerialNo: module.Module_No,
        palletNo: module.Pallet_No.toString(),
        binning: module.Binning,
        wattPeak: module.Module_Wattage,
        damagedDuring: '',
        uploadMRN: [],
        uploadEvidence: [],
        uploadVideo: [],
        claimFrom: '',
        contractor: '',
        reason: '',
        status: 'Not Submitted',
      })));
      setShowTable(true);
      setLoading(false);
      setResponseMessage({ type: 'success', text: 'Module details fetched successfully' });
    } catch (err) {
      setResponseMessage({ type: 'error', text: err.response?.data?.message || err.message });
      setLoading(false);
    }
  };

  const uploadChunk = async (file, fileId, chunkIndex, totalChunks, fileType, rowId, fileIndex, fileName) => {
    const formData = new FormData();
    formData.append('chunk', file);
    formData.append('isChunked', 'true');
    formData.append('fileId', fileId);
    formData.append('chunkIndex', chunkIndex);
    formData.append('totalChunks', totalChunks);
    formData.append('fileName', fileName);
    formData.append('fileType', fileType);
    formData.append('fileIndex', fileIndex);
    formData.append('projectId', projectId);
    formData.append('Module_No', rows.find((row) => row.id === rowId)?.moduleSerialNo || '');
    formData.append('Pallet_No', rows.find((row) => row.id === rowId)?.palletNo || '');
    formData.append('Module_Wattage', rows.find((row) => row.id === rowId)?.wattPeak || '');
    formData.append('Binning', rows.find((row) => row.id === rowId)?.binning || '');
    formData.append('damaged_during', rows.find((row) => row.id === rowId)?.damagedDuring || '');
    formData.append('claim_from', rows.find((row) => row.id === rowId)?.claimFrom || '');
    formData.append('contractor', rows.find((row) => row.id === rowId)?.contractor || '');
    formData.append('reason', rows.find((row) => row.id === rowId)?.reason || '');
    formData.append('submitted_by', sessionStorage.getItem('username') || '');

    try {
      console.log(`Uploading chunk ${chunkIndex}/${totalChunks} for file ${fileName}, fileType: ${fileType}`);
      const response = await apiClient.post(`submit_damaged_module_form`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      console.log('Chunk upload response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Chunk upload failed:', error.message, error.response?.data);
      throw error;
    }
  };

  const handleFileUpload = (rowId, field, files) => {
    const newFiles = Array.from(files);
    const fileType = field.replace('upload', '').toLowerCase();
    const allowedExtensions = {
      mrn: ALLOWED_MRN_EXTENSIONS,
      evidence: ALLOWED_EVIDENCE_EXTENSIONS,
      video: ALLOWED_VIDEO_EXTENSIONS,
    }[fileType];

    const validFiles = newFiles.filter((file) => {
      if (!file || !file.name || file.name === 'undefined') {
        setResponseMessage({
          type: 'error',
          text: `Invalid file name. Please select a valid file.`,
        });
        return false;
      }
      const fileExt = `.${file.name.split('.').pop().toLowerCase()}`;
      if (!allowedExtensions.includes(fileExt)) {
        setResponseMessage({
          type: 'error',
          text: `Invalid file type for ${fileType}: ${file.name}. Allowed: ${allowedExtensions.join(', ')}`,
        });
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setRows(rows.map((row) => row.id === rowId ? {
        ...row,
        [field]: [...(row[field] || []), ...validFiles.map(file => ({
          name: file.name || `unknown_${fileType}_${Date.now()}.${file.name.split('.').pop()}`,
          file,
        }))]
      } : row));
      setResponseMessage({ type: 'success', text: `Added ${validFiles.length} file(s) to upload queue` });
    }
  };

  const handleRemoveFile = (rowId, field, fileIndex) => {
    setRows(rows.map((row) => row.id === rowId ? {
      ...row,
      [field]: row[field].filter((_, index) => index !== fileIndex)
    } : row));
    setResponseMessage({ type: 'success', text: 'File removed successfully' });
  };

  const handleInputChange = (rowId, field, value) => {
    setRows(rows.map((row) => row.id === rowId ? { ...row, [field]: value } : row));
  };

  const handleSubmit = async (rowId) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;

    const requiredFields = {
      moduleSerialNo: 'Module Serial Number',
      wattPeak: 'Module Wattage',
      binning: 'Binning',
      damagedDuring: 'Damaged During',
      claimFrom: 'Claim From',
      reason: 'Reason',
    };
    const missingFields = Object.keys(requiredFields).filter((field) => !row[field]);
    if (missingFields.length > 0) {
      setResponseMessage({
        type: 'error',
        text: `Please fill in: ${missingFields.map((field) => requiredFields[field]).join(', ')}`,
      });
      return;
    }

    if (row.claimFrom === 'Contractor' && !row.contractor) {
      setResponseMessage({
        type: 'error',
        text: 'Contractor is required when Claim From is Contractor',
      });
      return;
    }

    if (!sessionStorage.getItem('username')) {
      setResponseMessage({
        type: 'error',
        text: 'User not logged in. Please log in to submit the form.',
      });
      return;
    }

    setRowLoading((prev) => ({ ...prev, [rowId]: true }));
    const uploadedFiles = { uploadMRN: [], uploadEvidence: [], uploadVideo: [] };

    for (const field of ['uploadMRN', 'uploadEvidence', 'uploadVideo']) {
      const fileType = field.replace('upload', '').toLowerCase();
      for (const [fileIndex, fileObj] of row[field].entries()) {
        const file = fileObj.file;
        const fileName = fileObj.name;
        if (file.size <= CHUNK_SIZE) {
          const formData = new FormData();
          formData.append('chunk', file);
          formData.append('isChunked', 'false');
          formData.append('fileName', fileName);
          formData.append('fileType', fileType);
          formData.append('fileIndex', fileIndex);
          formData.append('projectId', projectId);
          formData.append('Module_No', row.moduleSerialNo);
          formData.append('Pallet_No', row.palletNo);
          formData.append('Module_Wattage', row.wattPeak);
          formData.append('Binning', row.binning);
          formData.append('damaged_during', row.damagedDuring);
          formData.append('claim_from', row.claimFrom);
          formData.append('contractor', row.contractor || '');
          formData.append('reason', row.reason);
          formData.append('submitted_by', sessionStorage.getItem('username'));

          try {
            const response = await apiClient.post(`submit_damaged_module_form`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 60000,
            });
            if (response.data.status === 'success' && response.data.signedUrl) {
              uploadedFiles[field].push({ name: fileName, url: response.data.signedUrl });
            } else {
              throw new Error(response.data.message || `Failed to upload file ${fileName}`);
            }
          } catch (error) {
            setRowLoading((prev) => ({ ...prev, [rowId]: false }));
            setResponseMessage({
              type: 'error',
              text: error.response?.data?.error || `Failed to upload file ${fileName}`,
            });
            return;
          }
        } else {
          const fileId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
          let finalResponse = null;
          for (let i = 0; i < totalChunks; i++) {
            const start = i * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            const chunk = file.slice(start, end);
            try {
              const response = await uploadChunk(chunk, fileId, i, totalChunks, fileType, rowId, fileIndex, fileName);
              if (response.status === 'success' && response.signedUrl) {
                finalResponse = response;
              }
            } catch (error) {
              setRowLoading((prev) => ({ ...prev, [rowId]: false }));
              setResponseMessage({
                type: 'error',
                text: `Failed to upload chunk for ${fileName}`,
              });
              return;
            }
          }
          if (finalResponse && finalResponse.signedUrl) {
            uploadedFiles[field].push({ name: fileName, url: finalResponse.signedUrl });
          }
        }
      }
    }

    const formData = new FormData();
    formData.append('projectId', projectId);
    formData.append('Module_No', row.moduleSerialNo);
    formData.append('Pallet_No', row.palletNo);
    formData.append('Module_Wattage', row.wattPeak);
    formData.append('Binning', row.binning);
    formData.append('damaged_during', row.damagedDuring);
    formData.append('claim_from', row.claimFrom);
    formData.append('contractor', row.contractor || '');
    formData.append('reason', row.reason);
    formData.append('submitted_by', sessionStorage.getItem('username'));
    formData.append('isChunked', 'false');
    uploadedFiles.uploadMRN.forEach((file) => formData.append('upload_mrn[]', file.url));
    uploadedFiles.uploadEvidence.forEach((file) => formData.append('upload_evidence[]', file.url));
    uploadedFiles.uploadVideo.forEach((file) => formData.append('upload_video[]', file.url));
    uploadedFiles.uploadMRN.forEach((file) => formData.append('uploaded_filenames[]', file.name));
    uploadedFiles.uploadEvidence.forEach((file) => formData.append('uploaded_filenames[]', file.name));
    uploadedFiles.uploadVideo.forEach((file) => formData.append('uploaded_filenames[]', file.name));

    try {
      const response = await apiClient.post(`submit_damaged_module_form`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      setRows(rows.map((row) => row.id === rowId ? {
        ...row,
        status: 'Submitted',
        uploadMRN: uploadedFiles.uploadMRN,
        uploadEvidence: uploadedFiles.uploadEvidence,
        uploadVideo: uploadedFiles.uploadVideo,
      } : row));
      setResponseMessage({ type: 'success', text: response.data.message || 'Form submitted successfully' });
    } catch (error) {
      setResponseMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to submit form',
      });
    } finally {
      setRowLoading((prev) => ({ ...prev, [rowId]: false }));
    }
  };

  const downloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(rows.map((row) => ({
      'Sr.No': row.id,
      'Module Serial No': row.moduleSerialNo,
      'Pallet No': row.palletNo,
      Binning: row.binning,
      'Watt Peak': row.wattPeak,
      'Damaged During': row.damagedDuring,
      'Upload MRN': row.uploadMRN.map(file => file.name).join(', '),
      'Upload Evidence': row.uploadEvidence.map(file => file.name).join(', '),
      'Upload Video': row.uploadVideo.map(file => file.name).join(', '),
      'Claim From': row.claimFrom,
      Contractor: row.contractor,
      Reason: row.reason,
      Status: row.status,
    })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Damaged Items');
    XLSX.writeFile(workbook, 'DamagedItems.xlsx');
  };

  const tableHeaders = [
    { dbParam: 'id', displayName: 'Sr.No', className: 'w-16' },
    { dbParam: 'moduleSerialNo', displayName: 'Module Serial No', className: 'w-48' },
    { dbParam: 'palletNo', displayName: 'Pallet No', className: 'w-36' },
    { dbParam: 'binning', displayName: 'Binning', className: 'w-28' },
    { dbParam: 'wattPeak', displayName: 'Watt Peak', className: 'w-28' },
    { dbParam: 'damagedDuring', displayName: 'Damaged During', className: 'w-48' },
    { dbParam: 'uploadMRN', displayName: 'Upload MRN', className: 'w-64' },
    { dbParam: 'uploadEvidence', displayName: 'Upload Evidence', className: 'w-64' },
    { dbParam: 'uploadVideo', displayName: 'Upload Video', className: 'w-64' },
    { dbParam: 'claimFrom', displayName: 'Claim From', className: 'w-48' },
    { dbParam: 'contractor', displayName: 'Contractor', className: 'w-48' },
    { dbParam: 'reason', displayName: 'Reason', className: 'w-80' },
    { dbParam: 'status', displayName: 'Status', className: 'w-36' },
    { dbParam: 'action', displayName: 'Action', className: 'w-36' },
  ];

  const tableData = rows.map((row) => ({
    id: row.id,
    moduleSerialNo: row.moduleSerialNo,
    palletNo: row.palletNo,
    binning: row.binning,
    wattPeak: row.wattPeak,
    damagedDuring: (
      <SelectInput
        value={row.damagedDuring}
        onChange={(e) => handleInputChange(row.id, 'damagedDuring', e.target.value)}
        options={[
          { value: 'Internal Transit', label: 'Internal Transit' },
          { value: 'Installation', label: 'Installation' },
          { value: 'Unloading', label: 'Unloading' },
          { value: 'Internal Damage', label: 'Internal Damage' },
          { value: 'Defective', label: 'Defective' },
          { value: 'Others', label: 'Others' },
        ]}
        disabled={row.status === 'Submitted'}
        placeholder="Select"
      />
    ),
    uploadMRN: (
      <FileUpload
        rowId={row.id}
        field="uploadMRN"
        files={row.uploadMRN}
        handleFileUpload={handleFileUpload}
        handleRemoveFile={handleRemoveFile}
        disabled={row.status === 'Submitted'}
        label="Upload MRN"
        accept={ALLOWED_MRN_EXTENSIONS.join(',')}
      />
    ),
    uploadEvidence: (
      <FileUpload
        rowId={row.id}
        field="uploadEvidence"
        files={row.uploadEvidence}
        handleFileUpload={handleFileUpload}
        handleRemoveFile={handleRemoveFile}
        disabled={row.status === 'Submitted'}
        label="Upload Evidence"
        accept={ALLOWED_EVIDENCE_EXTENSIONS.join(',')}
      />
    ),
    uploadVideo: (
      <FileUpload
        rowId={row.id}
        field="uploadVideo"
        files={row.uploadVideo}
        handleFileUpload={handleFileUpload}
        handleRemoveFile={handleRemoveFile}
        disabled={row.status === 'Submitted'}
        label="Upload Video"
        accept={ALLOWED_VIDEO_EXTENSIONS.join(',')}
      />
    ),
    claimFrom: (
      <SelectInput
        value={row.claimFrom}
        onChange={(e) => handleInputChange(row.id, 'claimFrom', e.target.value)}
        options={[
          { value: 'Insurance', label: 'Insurance' },
          { value: 'Internal(No Claim)', label: 'Internal(No Claim)' },
          { value: 'Supplier', label: 'Supplier' },
          { value: 'Contractor', label: 'Contractor' },
        ]}
        disabled={row.status === 'Submitted'}
        placeholder="Select"
      />
    ),
    contractor: (
      <SelectInput
        value={row.contractor}
        onChange={(e) => handleInputChange(row.id, 'contractor', e.target.value)}
        options={contractors}
        disabled={row.status === 'Submitted' || !(row.claimFrom === 'Contractor' || row.claimFrom === 'Insurance' || row.claimFrom === 'Internal(No Claim)')}
        placeholder="Select"
      />
    ),
    reason: (
      <textarea
        value={row.reason}
        onChange={(e) => handleInputChange(row.id, 'reason', e.target.value)}
        className="border rounded-lg w-full bg-white focus:ring-2 focus:ring-gray-500 shadow-sm text-sm"
        rows="4"
        disabled={row.status === 'Submitted'}
      />
    ),
    status: (
      <span
        className={`px-4 py-2 rounded-full text-sm font-medium ${
          row.status === 'Submitted' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
        }`}
      >
        {row.status}
      </span>
    ),
    action: (
      <button
        onClick={() => handleSubmit(row.id)}
        className={`py-2 px-6 rounded-lg text-white font-medium transition-colors duration-200 shadow-sm text-sm flex items-center justify-center ${
          row.status === 'Submitted' || rowLoading[row.id] ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-600 hover:bg-gray-700'
        }`}
        disabled={row.status === 'Submitted' || rowLoading[row.id]}
      >
        {rowLoading[row.id] ? (
          <>
            <svg className="animate-spin h-5 w-5 mr-2 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Submitting...
          </>
        ) : (
          'Submit'
        )}
      </button>
    ),
  }));

  return (
    <div className="container mx-auto p-8 bg-gray-100 min-h-screen">
      <div className="max-w-full mx-auto">
        <h1 className="text-4xl font-bold text-gray-800 mb-8 text-center">Damaged Modules Information Form</h1>

        {responseMessage && (
          <div
            className={`p-4 mb-4 rounded-lg shadow-lg transition-opacity duration-500 ${
              responseMessage.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
            }`}
          >
            {responseMessage.text}
          </div>
        )}

        <div className="bg-white p-8 rounded-lg shadow-md mb-8">
          <h2 className="text-2xl font-semibold text-gray-700 mb-6">Add Module Serial Number</h2>
          <div className="flex gap-6 mb-6">
            <input
              type="text"
              value={serialNoInput}
              onChange={(e) => setSerialNoInput(e.target.value)}
              placeholder="Enter Module Serial Number"
              className="border p-3 rounded-lg flex-grow focus:ring-2 focus:ring-gray-500 focus:outline-none shadow-sm text-sm"
            />
            <button
              onClick={addSerialNumber}
              className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 shadow-sm text-sm"
            >
              Add
            </button>
          </div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-700">Added Serial Numbers</h2>
            {serialNumbers.length > 0 && (
              <button
                onClick={clearSerialNumbers}
                className="text-red-600 hover:text-red-700 font-medium text-sm"
              >
                Clear All
              </button>
            )}
          </div>
          {serialNumbers.length === 0 ? (
            <p className="text-gray-500 italic">No serial numbers added yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {serialNumbers.map((serial, index) => (
                <div key={index} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg shadow-sm">
                  <span className="text-gray-700 text-sm">{serial}</span>
                  <button
                    onClick={() => removeSerialNumber(serial)}
                    className="text-red-500 hover:text-red-700 font-medium text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={fetchModuleDetails}
          className={`w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-4 px-6 rounded-lg transition-colors duration-200 shadow-sm text-sm flex items-center justify-center ${
            serialNumbers.length === 0 || loading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          disabled={serialNumbers.length === 0 || loading}
        >
          {loading ? (
            <>
              <svg className="animate-spin h-6 w-6 mr-3 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Fetching Details...
            </>
          ) : (
            'Fetch Module Details'
          )}
        </button>

        {showTable && (
          <div className="mt-8 bg-white p-8 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-gray-700">Module Details</h2>
              <button
                onClick={downloadExcel}
                className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 shadow-sm text-sm"
                disabled={rows.length === 0}
              >
                Download Excel
              </button>
            </div>
            <div className="overflow-x-auto">
              <Table
                tableHeaders={tableHeaders}
                tableData={tableData}
                order="asc"
                orderBy="id"
                handleSort={(dbParam) => {
                  setRows([...rows].sort((a, b) => {
                    const aValue = a[dbParam] || '';
                    const bValue = b[dbParam] || '';
                    return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
                  }));
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DamagedForm;